import { Widget } from "@xsolla/login-sdk";
import { lStorage } from "./lstorage";
import { reachGoal } from "./ym";

const auth = {
    projectId: "1018115e-f7d3-4f60-8028-ddbdb13fa72b",
    callbackUrl: location.protocol + "//" + location.host + "/",
    clientId: 3508,
};

const xsollaOAuth2 = "https://login.xsolla.com/api/oauth2/token";
const xsollaMe = "https://login.xsolla.com/api/users/me";
export const xsollaSubscriptons = "https://subscriptions.xsolla.com​/api/user/v1/projects/184012/subscriptions";
export const xsollaPremiumId = "sumOxNVr";
export const xsollaItems = "https://store.xsolla.com/api/v2/project/184012/payment/item";
export const xsollaPayments = "https://secure.xsolla.com/paystation4/?token=";

interface RefreshToken {
    refresh_token: string,
}

interface Token extends RefreshToken {
    access_token: string,
    scope: string,
    expires_in: number,
    validUntilMs: number;
}

export interface Account {
    id: string,
    email: string,
    name: null | string,
    picture: null | string,
    token: Token,
}

type XsollaWidget = {
    show: () => void,
};
type LoginWidget = { elId: string, mounted: boolean } & Widget;

export function initWidget(el: HTMLDivElement): XsollaWidget {
    let widget: LoginWidget | null = null;

    return {
        show: () => {
            if (widget === null) {
                widget = new Widget({
                    ...auth,
                    responseType: "code",
                    state: encodeURIComponent(auth.callbackUrl),
                    redirectUri: auth.callbackUrl,
                    scope: "offline",
                    showPopupBackground: false,
                    showCloseButton: false,
                }) as LoginWidget;

                el.setAttribute("id", "xsolla-widget");
                widget.elId = el.id;
                widget.mounted = false;
            }

            if (!widget.mounted) {
                widget.mount(widget.elId);
                widget.mounted = true;
            }
        },
    };
}

export async function loadAccount(): Promise<Account | null> {
    async function responseToToken(response: Response): Promise<Token> {
        if (response.status !== 200) {
            throw new Error("Auth error (" + response.status + "): " + response.statusText);
        }

        const token: Token = await response.json();

        /* eslint-disable @typescript-eslint/no-explicit-any */
        if (typeof (token as any).error === "object") {
            throw new Error(JSON.stringify((token as any).error));
        }
        /* eslint-enable @typescript-eslint/no-explicit-any */

        if (typeof token.access_token !== "string" ||
        typeof token.refresh_token !== "string" ||
        typeof token.expires_in !== "number") {
            throw new Error("Unknown auth response: " + JSON.stringify(token));
        }

        token.validUntilMs = Date.now() + (token.expires_in - 5) * 1000;
        return token;
    }

    async function initToken() {
        const params = new URLSearchParams(location.search);
        const authCode = params.get("code");
        const authState = params.get("state");
        const authScope = params.get("scope");

        if (authCode || authScope || authState) {
            history.replaceState(null, "", auth.callbackUrl);
        }

        if (authCode !== null && authState !== null && authScope !== null) {
            reachGoal("login");
            const response = await fetch(xsollaOAuth2, {
                method: "POST",
                cache: "no-cache",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
                },
                body: `client_id=${auth.clientId}&code=${authCode}&state=${authState}` +
                `&grant_type=authorization_code&scope=${authScope}&redirect_uri=${auth.callbackUrl}`,

            });

            const token = await responseToToken(response);
            lStorage.setItem("token", JSON.stringify(token));
            return token;
        } else {
            const cachedToken = lStorage.getItem("token");
            if (cachedToken) {
                const token: Token = JSON.parse(cachedToken);
                if (token.validUntilMs <= Date.now()) {
                    const response = await fetch(xsollaOAuth2, {
                        method: "POST",
                        cache: "no-cache",
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
                        },
                        body: `client_id=${auth.clientId}&refresh_token=${token.refresh_token}` +
                    `&grant_type=refresh_token&redirect_uri=${auth.callbackUrl}`,

                    });
                    const freshToken = await responseToToken(response);
                    lStorage.setItem("token", JSON.stringify(freshToken));
                    return freshToken;
                } else if (token.refresh_token && token.access_token) {
                    return token;
                } else {
                    return null;
                }
            } else {
                return null;
            }
        }
    }

    try {
        const token = await initToken();
        if (token !== null) {
            const response = await fetch(xsollaMe, {
                method: "GET",
                cache: "no-cache",
                headers: {
                    Authorization: token.access_token,
                },
            });

            const data = await response.json();
            if (typeof data.error !== "undefined") {
                throw new Error(data.error.code + ": " + data.error.description);
            }

            const account = {
                token,
                id: data.id,
                name: data.name ?? data.first_name ?? null,
                email: data.email,
                picture: data.picture ?? null,
            };

            return account;
        }
    } catch (e) {
        console.error(e);
    }
    return null;
};

export function logout() {
    lStorage.removeItem("token");
    location.reload();
}

export async function linkToBuy(token: string): Promise<string | null> {
    const response = await fetch(xsollaSubscriptons + "/buy", {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Authorization": "Bearer " + token,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            plan_external_id: xsollaPremiumId,
        }),
    });

    const json = await response.json();

    if (json.link_to_ps === undefined) {
        console.error("Unexpected subscriptions response:", json);
        return null;
    }

    return json.link_to_ps;
}

export async function linkToBuyDays(token: string, id: "pass30" | "pass180"): Promise<string | null> {
    const response = await fetch(xsollaItems + "/" + id, {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Authorization": "Bearer " + token,
            "Content-Type": "application/json",
        },
    });

    const json = await response.json();

    if (json.token) {
        return xsollaPayments + json.token;
    } else {
        console.error("Unexpected items response:", json);
    }

    return null;
}

export async function cancelSubscription(token: string, subscription: string): Promise<boolean> {
    // eslint-disable-next-line
    // https://subscriptions.xsolla.com/api/doc/user#/Subscriptions/put_xsolla_subscription_apiuser_cancelusersubscription
    const response = await fetch(xsollaSubscriptons + "/" + subscription + "/cancel", {
        method: "PUT",
        cache: "no-cache",
        headers: {
            "Authorization": "Bearer " + token,
        },
    });

    const json = await response.json();
    if (json.error) {
        console.error("Unexpected subscriptions response:", json);
        return false;
    }

    return true;
}
